import React from "react";

import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import ProjectAPI from "@/src/api/ProjectAPI";
import Dialog from "@/src/components/dialog/Dialog";
import { useEmbedProfileMutations } from "@/src/hooks/mutations/useEmbedProfileMutations";

import type { JSX } from "react";

import type { EmbedProfile } from "@/src/api/ProjectAPI.types";

interface DeleteEmbedProfileDialogProps {
  profile: EmbedProfile | null;
  onClose: () => void;
}

export default function DeleteEmbedProfileDialog({
  profile,
  onClose,
}: DeleteEmbedProfileDialogProps): JSX.Element {
  const { t } = useTranslation("editor-organization");
  const { deleteEmbedProfile, isDeleting } = useEmbedProfileMutations();

  const usageQuery = ProjectAPI.generateQuery({
    fn: ProjectAPI.getEmbedProfileUsage,
    params: [profile?.uuid ?? ""],
  });
  const { data: usage, isError } = useQuery({
    ...usageQuery,
    enabled: profile !== null,
    cacheTime: 0,
  });

  const handleConfirm = (): void => {
    if (!profile) return;
    deleteEmbedProfile(profile.uuid, { onSettled: onClose });
  };

  const handleOpenChange = (next: boolean): void => {
    if (!next) onClose();
  };

  return (
    <Dialog
      cancelLabel={t("editor-offers:cancel")}
      confirmLabel={t("embedProfiles.actions.delete")}
      confirmVariant="destructive"
      contentClassName="sm:max-w-sm"
      isConfirmDisabled={!usage || isDeleting}
      isOpen={profile !== null}
      onConfirm={handleConfirm}
      onOpenChange={handleOpenChange}
      title={t("embedProfiles.delete.title")}
    >
      <div className="text-muted-foreground flex flex-col gap-2 text-sm">
        <p>{t("embedProfiles.delete.confirm", { name: profile?.label })}</p>
        {isError ? (
          <p className="text-destructive">
            {t("embedProfiles.delete.usageError")}
          </p>
        ) : null}
        {usage ? (
          <React.Fragment>
            <p>
              {t("embedProfiles.delete.linked", { count: usage.medias_count })}
            </p>
            {profile?.default ? (
              <p>
                {t("embedProfiles.delete.inherited", {
                  count: usage.inherited_medias_count,
                })}
              </p>
            ) : null}
          </React.Fragment>
        ) : null}
      </div>
    </Dialog>
  );
}
